/**
 * Venue card + facility icon rendering (web).
 *
 * Places is venue-led: the question is "where's a pub with a pool table?",
 * so the facility row is not decoration, it is the answer. It sits directly
 * under the name, above the fold of the card, rendered as ICONS so a row of
 * six features costs one line instead of six pills.
 *
 * `facility_definitions.icon` holds a kebab-case **lucide icon name**
 * ("circle-dot", "target", "trees"). VenueDetail used to render that column
 * as a character — `{icon.length <= 2 ? icon : "✓"}` — which meant every real
 * definition fell through to a tick. `facilityIcon()` in ./venue-utils maps
 * the name to the component it always referred to.
 */
import { Link } from "wouter";
import { Beer, MapPin, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Chip } from "./primitives";
import { ELEVATION, FOCUS_RING, RADII, TYPE } from "./tokens";
import { facilityIcon, venueHref, venueTypeLabel, venueTypeTone } from "./venue-utils";
import type { FacilityDefinition, HubVenue } from "./hub-data";

/* ── Facility icon row ────────────────────────────────────────────────── */

export interface FacilityChipItem {
  slug: string;
  name: string;
  icon: string;
}

/**
 * Resolve slugs to display items using the controlled vocabulary.
 * Unknown slugs are dropped rather than rendered raw — a venue carrying a
 * facility whose definition was deactivated should go quiet, not show
 * "pool-table" to a customer.
 */
export function resolveFacilities(
  slugs: Iterable<string>,
  definitions: readonly FacilityDefinition[],
): FacilityChipItem[] {
  const byslug = new Map(definitions.map((d) => [d.slug, d]));
  const out: FacilityChipItem[] = [];
  for (const slug of slugs) {
    const def = byslug.get(slug);
    if (def) out.push({ slug, name: def.name, icon: def.icon });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Icon + label row for a card. Overflow collapses to "+3". */
export function FacilityIconRow({
  facilities, max = 4, className,
}: { facilities: readonly FacilityChipItem[]; max?: number; className?: string }) {
  if (facilities.length === 0) return null;
  const shown = facilities.slice(0, max);
  const rest = facilities.length - shown.length;

  return (
    <ul className={cn("flex items-center gap-1.5 flex-wrap", className)}>
      {shown.map((f) => {
        const Icon = facilityIcon(f.icon);
        return (
          <li
            key={f.slug}
            title={f.name}
            className={cn(
              TYPE.footnote, RADII.xs,
              "inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 border border-slate-200/70 text-slate-600",
            )}
          >
            <Icon size={12} className="text-[#1e3a5f]" aria-hidden />
            <span className="whitespace-nowrap">{f.name}</span>
          </li>
        );
      })}
      {rest > 0 && (
        <li className={cn(TYPE.footnote, "text-slate-400")} title={facilities.slice(max).map((f) => f.name).join(", ")}>
          +{rest}
        </li>
      )}
    </ul>
  );
}

/** Full, grouped facility display for the venue detail page. */
export function FacilityGrid({
  facilities, definitions,
}: { facilities: readonly FacilityChipItem[]; definitions: readonly FacilityDefinition[] }) {
  if (facilities.length === 0) return null;

  const categoryBySlug = new Map(definitions.map((d) => [d.slug, d.category]));
  const grouped = new Map<string, FacilityChipItem[]>();
  for (const f of facilities) {
    const cat = categoryBySlug.get(f.slug) ?? "other";
    const bucket = grouped.get(cat);
    if (bucket) bucket.push(f);
    else grouped.set(cat, [f]);
  }

  const CATEGORY_LABELS: Record<string, string> = {
    games: "Games", sports: "Sport", entertainment: "Entertainment",
    outdoor: "Outdoor", venue: "The venue", dining: "Food & drink",
    access: "Access", other: "Other",
  };

  return (
    <div className="space-y-4">
      {[...grouped.entries()].map(([category, items]) => (
        <div key={category}>
          <p className={cn(TYPE.overline, "text-slate-400 mb-2")}>
            {CATEGORY_LABELS[category] ?? category}
          </p>
          <div className="grid grid-cols-2 min-[560px]:grid-cols-3 lg:grid-cols-4 gap-2">
            {items.map((f) => {
              const Icon = facilityIcon(f.icon);
              return (
                <div
                  key={f.slug}
                  className={cn(
                    RADII.sm, TYPE.callout,
                    "flex items-center gap-2 px-3 py-2.5 bg-white border border-slate-200/80 text-slate-700",
                  )}
                >
                  <Icon size={16} className="text-[#1e3a5f] shrink-0" aria-hidden />
                  <span className="truncate">{f.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Venue card ───────────────────────────────────────────────────────── */

export function VenueCard({
  venue, facilities, whatsOnLine, className,
}: {
  venue: HubVenue;
  facilities: readonly FacilityChipItem[];
  /** One-line "what's on here" teaser, resolved by reference — never stored on the venue. */
  whatsOnLine?: string | null;
  className?: string;
}) {
  return (
    <Link
      href={venueHref(venue)}
      className={cn("group block h-full", FOCUS_RING, RADII.md, className)}
      aria-label={[venue.name, venueTypeLabel(venue.venueType), venue.town].filter(Boolean).join(", ")}
    >
      <article
        className={cn(
          RADII.md, ELEVATION.sm,
          "h-full flex flex-col overflow-hidden bg-white border transition-all duration-200",
          "hover:shadow-[0_4px_16px_rgba(15,23,42,0.12)] md:hover:-translate-y-0.5",
          venue.isFeatured ? "border-amber-300" : "border-slate-200/80",
        )}
      >
        <div className="relative aspect-[21/9] sm:aspect-[16/10] bg-slate-100 overflow-hidden">
          {venue.coverPhoto ? (
            <img
              src={venue.coverPhoto}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 md:group-hover:scale-105"
            />
          ) : (
            /* No venue has a cover photo yet (all 3 are empty strings —
               MIGRATION_REPORT §1.4). A branded gradient reads as "no photo
               yet" rather than as a broken image, and keeps the card
               silhouette identical to one that does have a photo. */
            <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#0c1e33] to-[#1e3a5f]">
              <Beer size={26} className="text-white/35" aria-hidden />
            </div>
          )}

          <div
            className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/45 to-transparent pointer-events-none"
            aria-hidden
          />
          <div className="absolute top-2 left-2 flex gap-1">
            <Chip variant="overlay" size="xs">{venueTypeLabel(venue.venueType)}</Chip>
            {venue.isFeatured && (
              <Chip variant="overlay" size="xs" icon={<Star size={10} fill="currentColor" aria-hidden />}>
                Featured
              </Chip>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-1.5 p-3">
          <h3 className={cn(TYPE.headline, "text-slate-900 line-clamp-1")}>{venue.name}</h3>

          <p className={cn(TYPE.subhead, "text-slate-500 line-clamp-1 inline-flex items-center gap-1")}>
            <MapPin size={11} className="shrink-0" aria-hidden />
            {[venue.town, venue.address].filter(Boolean).join(" · ") || "Ayrshire"}
          </p>

          {/* The answer to "what's in there". Directly under the name because
              on this page it is the reason the card is in the list at all. */}
          <FacilityIconRow facilities={facilities} className="mt-0.5" />

          {whatsOnLine && (
            <p className={cn(TYPE.footnote, "text-[#1e3a5f] mt-auto pt-1.5 line-clamp-1 font-medium")}>
              {whatsOnLine}
            </p>
          )}
        </div>
      </article>
    </Link>
  );
}

export function VenueCardSkeleton() {
  return (
    <div className={cn(RADII.md, ELEVATION.sm, "overflow-hidden bg-white border border-slate-200/80")}>
      <div className="aspect-[21/9] sm:aspect-[16/10] skeleton-shimmer bg-slate-200/70" />
      <div className="p-3 space-y-2">
        <div className="h-3.5 w-2/3 rounded skeleton-shimmer bg-slate-200/70" />
        <div className="h-3 w-1/2 rounded skeleton-shimmer bg-slate-200/70" />
        <div className="h-4 w-4/5 rounded skeleton-shimmer bg-slate-200/70" />
      </div>
    </div>
  );
}

/** Venue chip used inside What's On empty states ("venues that regularly have live music"). */
export function VenueMiniCard({
  venue, facilities,
}: { venue: HubVenue; facilities: readonly FacilityChipItem[] }) {
  return (
    <Link
      href={venueHref(venue)}
      className={cn(
        RADII.md, FOCUS_RING,
        "flex items-center gap-3 bg-white border border-slate-200/80 p-3 transition-colors hover:border-slate-300",
      )}
    >
      <div className={cn(RADII.sm, "w-11 h-11 shrink-0 grid place-items-center bg-gradient-to-br from-[#0c1e33] to-[#1e3a5f] overflow-hidden")}>
        {venue.coverPhoto
          ? <img src={venue.coverPhoto} alt="" loading="lazy" className="w-full h-full object-cover" />
          : <Beer size={18} className="text-white/40" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn(TYPE.headline, "text-slate-900 line-clamp-1")}>{venue.name}</p>
        <p className={cn(TYPE.footnote, "text-slate-500 line-clamp-1")}>
          {[venueTypeLabel(venue.venueType), venue.town].filter(Boolean).join(" · ")}
        </p>
      </div>
      <FacilityIconRow facilities={facilities} max={2} className="hidden sm:flex shrink-0" />
    </Link>
  );
}
